import registryJson from "./generated/action-capability-registry.v1.json" with {
  type: "json",
};
import type {
  ActionCapability,
  ActionCapabilityRegistry,
  ActionDef,
} from "./types";
import { bindActionSemanticsLookup, deriveActionSemantics } from "./semantics";
import {
  assertActionConsumerCapabilityCoverage,
  getActionConsumerContract,
  RUNTIME_ACTION_CONSUMER_CATALOG,
} from "./action-consumer-projection";

const registry = registryJson as unknown as ActionCapabilityRegistry;

export const ACTION_CAPABILITY_REGISTRY_SCHEMA_VERSION = registry.schemaVersion;
export const ACTION_CAPABILITY_REGISTRY_SOURCES: readonly string[] =
  Object.freeze([...registry.sources]);

assertActionConsumerCapabilityCoverage(
  registry.actions
    .filter((capability) => capability.runtimeImplemented)
    .map((capability) => capability.name),
  RUNTIME_ACTION_CONSUMER_CATALOG,
);

export const ACTION_CAPABILITY_LIST: readonly ActionCapability[] =
  registry.actions.map((capability) => {
    const consumerContract = getActionConsumerContract(capability.name);
    if (capability.runtimeImplemented !== Boolean(consumerContract)) {
      throw new Error(`ACTION_CONSUMER_CAPABILITY_COVERAGE:${capability.name}`);
    }
    return {
      ...capability,
      semantics: capability.semantics ?? deriveActionSemantics(capability),
      ...(consumerContract
        ? {
            consumerCatalogDigest: RUNTIME_ACTION_CONSUMER_CATALOG.catalogDigest,
            consumerContract,
          }
        : {}),
    };
  });

export const ACTION_CAPABILITIES: Readonly<Record<string, ActionCapability>> =
  Object.freeze(
    Object.fromEntries(
      ACTION_CAPABILITY_LIST.map((capability) => [capability.name, capability]),
    ),
  );

/** Compatibility projection for editor and validator consumers. */
export const ACTION_DEFS: Readonly<Record<string, ActionDef>> = Object.freeze(
  Object.fromEntries(
    ACTION_CAPABILITY_LIST.filter(
      (capability) => capability.registryExposure === "public",
    ).map((capability) => [capability.name, toActionDef(capability)]),
  ),
);

bindActionSemanticsLookup(
  Object.freeze(
    Object.fromEntries(
      ACTION_CAPABILITY_LIST.flatMap((capability) => [
        [capability.name, capability.semantics],
        ...capability.aliases.map(
          (alias) => [alias, capability.semantics] as const,
        ),
      ]),
    ),
  ),
);

export function getActionCapability(
  actionName: string,
): ActionCapability | undefined {
  return ACTION_CAPABILITIES[actionName];
}

export function getActionsByCategory(category: string): ActionDef[] {
  return Object.values(ACTION_DEFS).filter(
    (action) => action.category === category,
  );
}

export function getActionsByDomain(domain: string): ActionDef[] {
  return Object.values(ACTION_DEFS).filter(
    (action) => action.semantics.domain === domain,
  );
}

export function getActionSemantics(
  actionName: string,
): ActionDef["semantics"] | undefined {
  return ACTION_DEFS[actionName]?.semantics;
}

export function getActionParams(actionName: string): ActionDef["params"] {
  return ACTION_DEFS[actionName]?.params ?? [];
}

export function isValidAction(name: string): boolean {
  return name in ACTION_DEFS;
}

export function getActionsForPlatform(
  platform: "web" | "reactNative" | "ssr",
): ActionDef[] {
  return Object.values(ACTION_DEFS).filter(
    (action) => action.platform[platform],
  );
}

export function getCategories(): string[] {
  return [
    ...new Set(Object.values(ACTION_DEFS).map((action) => action.category)),
  ];
}

function toActionDef(capability: ActionCapability): ActionDef {
  const {
    aliases: _aliases,
    canonicalAction: _canonicalAction,
    ontologyIri: _ontologyIri,
    lifecycle: _lifecycle,
    registryExposure: _registryExposure,
    runtimeImplemented: _runtimeImplemented,
    targetSupport: _targetSupport,
    generationPolicy: _generationPolicy,
    grounding: _grounding,
    evidence: _evidence,
    securityClass: _securityClass,
    consumerCatalogDigest: _consumerCatalogDigest,
    consumerContract: _consumerContract,
    ...definition
  } = capability;
  return definition;
}
